import { useCallback, useEffect, useRef, useState } from 'react';
import { V2Button } from '@sakhtar/design-system/react';
import { apiPost, ApiError } from '../../lib/apiClient.js';
import type { ChequeScanResult } from '../../lib/types/finance.js';
import {
  cameraConstraintAttempts,
  cameraErrorMessage,
  cameraSupport,
  isImageDataUrl,
  validateChequeImageFile,
  type CameraFacing,
} from './cheque-camera.js';
import './cheques.css';

interface ChequeCameraScannerProps {
  readonly isOpen: boolean;
  /** Full tenant-scoped URL, e.g. `/t/acme/api/v1/finance/treasury/cheques/scan-ai`. */
  readonly scanUrl: string;
  readonly onClose: () => void;
  readonly onExtracted: (result: ChequeScanResult, previewImage: string) => void;
}

export function ChequeCameraScanner({
  isOpen,
  scanUrl,
  onClose,
  onExtracted,
}: ChequeCameraScannerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const requestSeq = useRef(0);

  const [stream, setStream] = useState<MediaStream | null>(null);
  const [facing, setFacing] = useState<CameraFacing>('environment');
  const [error, setError] = useState<string | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [starting, setStarting] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const releaseStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setStream(null);
  }, []);

  // Open / re-open the camera whenever the dialog is open, nothing is captured,
  // and facing or a manual retry changes. The seq guard drops stale getUserMedia results.
  useEffect(() => {
    if (!isOpen || capturedImage) {
      releaseStream();
      return;
    }
    const seq = ++requestSeq.current;
    const support = cameraSupport({
      isSecureContext: typeof window !== 'undefined' ? window.isSecureContext : undefined,
      mediaDevices: typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined,
    });
    if (support !== 'ok') {
      setError(cameraErrorMessage(null, support));
      return;
    }
    setError(null);
    setStarting(true);
    void (async () => {
      let lastErr: unknown = null;
      for (const constraints of cameraConstraintAttempts(facing)) {
        try {
          const s = await navigator.mediaDevices.getUserMedia(constraints);
          if (seq !== requestSeq.current) {
            s.getTracks().forEach((t) => t.stop());
            return;
          }
          streamRef.current?.getTracks().forEach((t) => t.stop());
          streamRef.current = s;
          setStream(s);
          setStarting(false);
          return;
        } catch (err) {
          lastErr = err;
          // Permission problems will not be fixed by relaxing constraints.
          if (err instanceof Error && /NotAllowed|Security|PermissionDenied/.test(err.name)) break;
        }
      }
      if (seq !== requestSeq.current) return;
      setStarting(false);
      setError(cameraErrorMessage(lastErr));
    })();
    return () => {
      requestSeq.current += 1;
    };
  }, [isOpen, capturedImage, facing, attempt, releaseStream]);

  // The <video> element only exists once the dialog renders; bind the stream to it here.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.srcObject = stream;
    if (stream) void video.play().catch(() => undefined);
  }, [stream]);

  // Always release the camera on unmount.
  useEffect(() => releaseStream, [releaseStream]);

  // Reset state when the dialog closes so the next open starts clean.
  useEffect(() => {
    if (!isOpen) {
      setCapturedImage(null);
      setError(null);
      setIsProcessing(false);
    }
  }, [isOpen]);

  const processImage = async (base64: string) => {
    setIsProcessing(true);
    setError(null);
    try {
      const response = await apiPost<ChequeScanResult>(scanUrl, { imageBase64: base64 });
      if (!response.success) {
        setError(
          response.error ||
            'استخراج اطلاعات چک ناموفق بود. کیفیت تصویر کافی نیست یا سرویس پیکربندی نشده است.',
        );
        setIsProcessing(false);
        return;
      }
      setIsProcessing(false);
      onExtracted(response, base64);
      onClose();
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'خطا در پردازش تصویر چک');
      setIsProcessing(false);
    }
  };

  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const base64 = canvas.toDataURL('image/jpeg', 0.88);
    setCapturedImage(base64);
    releaseStream();
    void processImage(base64);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const problem = validateChequeImageFile(file);
    if (problem) {
      setError(problem);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = typeof reader.result === 'string' ? reader.result : '';
      if (!isImageDataUrl(base64)) {
        setError('فرمت تصویر پشتیبانی نمی‌شود. از JPG یا PNG استفاده کنید.');
        return;
      }
      setCapturedImage(base64);
      releaseStream();
      void processImage(base64);
    };
    reader.onerror = () => setError('خواندن فایل ناموفق بود.');
    reader.readAsDataURL(file);
  };

  const handleRetake = () => {
    setCapturedImage(null);
    setError(null);
    setIsProcessing(false);
  };

  if (!isOpen) return null;

  const cameraActive = stream !== null && !capturedImage;

  return (
    <div className="chq-overlay" style={{ zIndex: 9999, background: 'rgba(0,0,0,0.85)' }}>
      <div
        className="chq-cam"
        role="dialog"
        aria-modal="true"
        aria-labelledby="chq-cam-title"
        data-testid="cheque-scanner"
      >
        <div className="chq-cam-head">
          <div>
            <h3 id="chq-cam-title">اسکن هوشمند چک صیادی</h3>
            <p>شناسه ۱۶ رقمی، تاریخ و مبلغ به‌صورت خودکار استخراج می‌شود</p>
          </div>
          <button
            type="button"
            className="chq-close"
            style={{ color: '#94a3b8' }}
            onClick={onClose}
            aria-label="بستن"
          >
            ✕
          </button>
        </div>

        <div className="chq-cam-view">
          {capturedImage ? (
            <img src={capturedImage} alt="پیش‌نمایش چک" />
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              data-testid="cheque-video"
              style={{ display: cameraActive ? 'block' : 'none' }}
            />
          )}
          {cameraActive && (
            <div className="chq-cam-guide">
              <span>چک صیادی را درون کادر قرار دهید</span>
            </div>
          )}
          {!capturedImage && !cameraActive && (
            <div className="chq-cam-msg" style={{ position: 'absolute' }}>
              {starting ? (
                <p style={{ color: '#e2e8f0' }}>در حال اتصال به دوربین…</p>
              ) : (
                <>
                  <p role="alert">{error ?? 'دوربین فعال نیست'}</p>
                  <V2Button variant="primary" onClick={() => setAttempt((n) => n + 1)}>
                    تلاش مجدد اتصال دوربین
                  </V2Button>
                </>
              )}
            </div>
          )}
          {capturedImage && error && !isProcessing && (
            <div className="chq-cam-busy">
              <p role="alert" style={{ margin: 0, color: '#fca5a5' }}>
                {error}
              </p>
            </div>
          )}
          {isProcessing && (
            <div className="chq-cam-busy" role="status">
              <div className="chq-cam-spinner" />
              <p style={{ margin: 0 }}>در حال استخراج اطلاعات چک…</p>
            </div>
          )}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>

        {!capturedImage && error && cameraActive && (
          <p role="alert" style={{ margin: 0, padding: '8px 16px', color: '#fca5a5' }}>
            {error}
          </p>
        )}

        <div className="chq-cam-foot">
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              data-testid="cheque-file-input"
            />
            <V2Button
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={isProcessing}
            >
              انتخاب از گالری / فایل
            </V2Button>
            {cameraActive && (
              <V2Button
                variant="secondary"
                onClick={() => setFacing((f) => (f === 'environment' ? 'user' : 'environment'))}
                disabled={isProcessing}
              >
                تغییر دوربین
              </V2Button>
            )}
          </div>
          <div>
            {capturedImage ? (
              <V2Button variant="secondary" onClick={handleRetake} disabled={isProcessing}>
                عکس مجدد
              </V2Button>
            ) : (
              <V2Button variant="primary" onClick={handleCapture} disabled={!cameraActive}>
                گرفتن عکس و استخراج اطلاعات
              </V2Button>
            )}
            <V2Button variant="secondary" onClick={onClose} disabled={isProcessing}>
              انصراف
            </V2Button>
          </div>
        </div>
      </div>
    </div>
  );
}
